import { Alert, Skeleton } from '@hvac/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { customerQuery } from '../../shared/api/parties-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { AtAGlancePanel, ObjectTimeline, RelationshipsPanel } from '../objects/object-panels.js';

/**
 * One customer, as a place. The header states who they are; the shared object
 * panels, the same two every object gets, carry the substance.
 */
export function CustomerDetailPage() {
  const { customerId } = useParams({ from: '/protected/customers/$customerId' });
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const customer = useQuery(customerQuery(organizationId, customerId));

  if (customer.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (customer.isError) {
    return (
      <Alert tone="danger" title="That customer could not be loaded">
        {customer.error.message}
      </Alert>
    );
  }

  const data = customer.data;
  const contact = [data.primaryPhone, data.primaryEmail, data.billingCity]
    .filter((part) => part !== null)
    .join(' · ');

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/customers" className="text-sm text-content-muted hover:text-content">
          ← Customers
        </Link>
        <h1 className="mt-1 text-xl font-semibold text-content">{data.displayName}</h1>
        <p className="text-sm text-content-muted">
          {contact === '' ? 'No contact details on file.' : contact}
          {data.isTaxExempt ? ' · tax-exempt' : ''}
        </p>
      </div>

      {data.notes === null ? null : <p className="text-sm text-content-muted">{data.notes}</p>}

      <AtAGlancePanel organizationId={organizationId} entityType="customer" entityId={data.id} />

      <RelationshipsPanel
        organizationId={organizationId}
        entityType="customer"
        entityId={data.id}
      />

      <ObjectTimeline
        organizationId={organizationId}
        resourceType="customer"
        resourceId={data.id}
      />
    </div>
  );
}
